import {FC, useState} from "react";
import {StyledIconButton} from "@/components/styled";
import SvgUserEdit from "@/assets/icons/SVGUserEdit.tsx";
import {Alert, Box, Button, MenuItem, Typography, useMediaQuery, useTheme} from "@mui/material";
import Modal from "@/components/Modal";
import FormSelect from "@/components/FormSelect";
import dayjs from "dayjs";
import {getQuartersForYear} from "@/utils/getQuartersForYear.ts";
import {getMonthsForQuarter} from "@/utils/getMonthsForQuarter.ts";
import {updatePerformanceReportReq} from "@/api/projectsApi";
import {useForm} from "react-hook-form";
import Snackbar from "@mui/material/Snackbar";

type NotificationMessage = {
    message: string;
    severity: "error" | "success";
}

type FormValues = {
    year: string;
    quarter: string;
};

type Props = {
    projectId: number;
    performanceReportId: number;
    quarterName: string | null;
    performanceYear: number | null;
    getFilesReq: () => void;
}

const EditPerformanceReportFile:FC<Props> = ({  projectId, performanceReportId, quarterName, performanceYear, getFilesReq }) => {
    const [notificationMessage, setNotificationMessage] = useState<NotificationMessage | null>(null);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const theme = useTheme();
    const downSm = useMediaQuery(theme.breakpoints.down("sm"));

    const {
        handleSubmit,
        control,
        watch,
        formState: { isValid },
    } = useForm<FormValues>({
        mode: "onChange",
        defaultValues: {
            year: String(performanceYear ?? ""),
            quarter: quarterName ?? "",
        },
    });

    const selectedYear = watch("year");

    const toggleModal = () => {
        setIsOpenModal((prev) => !prev);
    }

    const handleCloseNotification = () => {
        setNotificationMessage(null);
    }

    const onSubmit = async (data: FormValues) => {
        try {
            const response = await updatePerformanceReportReq({
                projectId,
                performanceReportId,
                quarterName: data.quarter,
                performanceYear: Number(data.year)
            });

            getFilesReq()

            setNotificationMessage({
                message: response?.data?.message,
                severity: "success",
            });
        } catch (e: any) {
            setNotificationMessage({
                message: e?.response?.data?.error || "An unexpected error occurred.",
                severity: "error",
            });
        } finally {
            toggleModal();
        }
    };

    return (
        <>
            <StyledIconButton
                sx={{ ...(downSm && { flex: 1 }) }}
                onClick={toggleModal}
                variant={!downSm ? "text" : "outlined"}
            >
                {!downSm ? <SvgUserEdit color="#1976d2" strokeWidth={1.5} weight={22} height={22} /> : "Edit Performance Report"}
            </StyledIconButton>
            <Modal open={isOpenModal} onClose={toggleModal} title="Edit Performance Report">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormSelect
                        control={control}
                        name="year"
                        label="Year"
                        placeholder="Year"
                    >
                        {Array.from({ length: 5 }, (_, i) => dayjs().year() - i).map((year) => (
                            <MenuItem key={year} value={String(year)}>
                                {year}
                            </MenuItem>
                        ))}
                    </FormSelect>

                    <FormSelect
                        control={control}
                        name="quarter"
                        label="Quarter"
                        placeholder="Quarter"
                        disabled={!selectedYear}
                    >
                        {getQuartersForYear(Number(selectedYear)).map((quarter) => (
                            <MenuItem key={quarter} value={quarter}>
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Typography fontWeight={600}>{quarter}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {getMonthsForQuarter(quarter).join(", ")}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </FormSelect>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!isValid}
                    >
                        Save
                    </Button>
                </form>
            </Modal>
            {notificationMessage && (
                <Snackbar
                    open={Boolean(notificationMessage)}
                    autoHideDuration={6000}
                    onClose={handleCloseNotification}
                >
                    <Alert onClose={handleCloseNotification} severity={notificationMessage?.severity}>
                        {notificationMessage?.message}
                    </Alert>
                </Snackbar>
            )}
        </>
    )
}

export default EditPerformanceReportFile;